// WP-5.2 (M17 MI-1..5, S-25 `/intel`): Money Intelligence — left rail (dimension picker +
// currency/period + compare toggle) + main (TrendChart + top-N breakdown table) + Save view.
// "Every bar/row drills to /tx filtered" is a documented scope trim: the Transactions page
// (S-09) doesn't read any URL filter params yet — that's a separate, sizeable feature — so
// rows link to the plain, unfiltered `/tx` for now, the same "Adjust links to the plain
// transaction list" trim InboxItem.tsx already documents for a similar reason.
import type { CurrencyCode, MiDimension, MiDirection } from '@pfos/engine';
import { MI_EXPENSE_DIMENSIONS, MI_INCOME_DIMENSIONS } from '@pfos/engine';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { ghostButtonInlineClass, inputClass, selectClass } from '../components/FormKit';
import {
  useMiBreakdown,
  useCreateSavedView,
  useDeleteSavedView,
  useSavedViews,
} from '../lib/intel';
import { useReport } from '../lib/reports';
import { useTags } from '../lib/tags';

const DIMENSION_LABELS: Record<MiDimension, string> = {
  client: 'Client',
  project: 'Project',
  serviceCategory: 'Service category',
  productCategory: 'Product category',
  account: 'Account',
  earningModel: 'Earning model',
  category: 'Category',
};

type PeriodMode = 'month' | 'year';

function currentPeriod(mode: PeriodMode): { from: string; to: string } {
  const now = new Date();
  if (mode === 'month') {
    return {
      from: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString(),
      to: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)).toISOString(),
    };
  }
  return {
    from: new Date(Date.UTC(now.getUTCFullYear(), 0, 1)).toISOString(),
    to: new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1)).toISOString(),
  };
}

function priorPeriod(
  mode: PeriodMode,
  period: { from: string; to: string },
): { from: string; to: string } {
  const from = new Date(period.from);
  if (mode === 'month') {
    return {
      from: new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() - 1, 1)).toISOString(),
      to: period.from,
    };
  }
  return {
    from: new Date(Date.UTC(from.getUTCFullYear() - 1, 0, 1)).toISOString(),
    to: period.from,
  };
}

interface MiViewConfig {
  direction: MiDirection;
  dimension: MiDimension;
  currency: CurrencyCode;
  periodMode: PeriodMode;
  compare: boolean;
  topN: number | null;
  tag?: string; // WP-C1.8 (SR-2)
}

function TrendMiniChart({
  rows,
  direction,
}: {
  rows: Record<string, string | number>[];
  direction: MiDirection;
}): React.JSX.Element | null {
  if (rows.length === 0) return null;
  const key = direction === 'income' ? 'incomeMinor' : 'expensesMinor';
  const values = rows.map((r) => Number(r[key] ?? 0));
  const max = Math.max(...values.map(Math.abs), 1);
  return (
    <div className="mb-4 space-y-1">
      {rows.map((row, i) => {
        const value = values[i] ?? 0;
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="w-16 shrink-0 truncate text-xs text-muted">
              {String(row.month ?? '')}
            </span>
            <div className="h-3 flex-1 rounded bg-bg">
              <div
                className="h-3 rounded bg-primary"
                style={{ width: `${String((Math.abs(value) / max) * 100)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MoneyIntelligencePage(): React.JSX.Element {
  const [direction, setDirection] = useState<MiDirection>('income');
  const [dimension, setDimension] = useState<MiDimension>('client');
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('year');
  const [compare, setCompare] = useState(false);
  const [topN, setTopN] = useState<number | null>(10);
  const [tag, setTag] = useState('');
  const [viewName, setViewName] = useState('');
  const { data: tags } = useTags();

  const dimensions = direction === 'income' ? MI_INCOME_DIMENSIONS : MI_EXPENSE_DIMENSIONS;

  useEffect(() => {
    if (!(dimensions as readonly string[]).includes(dimension)) setDimension(dimensions[0]);
    // Deliberately depends on `direction` alone — reacting to `dimensions`/`dimension` too
    // would fire on every dimension pick, defeating the point of this reset-on-switch effect.
  }, [direction]);

  const period = currentPeriod(periodMode);
  const prior = priorPeriod(periodMode, period);

  const { data: breakdown, isLoading } = useMiBreakdown({
    direction,
    dimension,
    currency,
    from: period.from,
    to: period.to,
    tag,
  });
  const { data: priorBreakdown } = useMiBreakdown({
    direction,
    dimension,
    currency,
    from: prior.from,
    to: prior.to,
    tag,
  });
  const { data: trend } = useReport('cashflow', { currency, yearBasis: 'calendar' });

  const savedViews = useSavedViews();
  const createView = useCreateSavedView();
  const deleteView = useDeleteSavedView();

  const priorByKey = useMemo(
    () => new Map((priorBreakdown?.rows ?? []).map((r) => [r.key, r.amountMinor])),
    [priorBreakdown],
  );
  const rows = useMemo(() => {
    const all = breakdown?.rows ?? [];
    return topN !== null && topN > 0 ? all.slice(0, topN) : all;
  }, [breakdown, topN]);

  const applyView = (config: Record<string, unknown>): void => {
    const c = config as Partial<MiViewConfig>;
    if (c.direction !== undefined) setDirection(c.direction);
    if (c.dimension !== undefined) setDimension(c.dimension);
    if (c.currency !== undefined) setCurrency(c.currency);
    if (c.periodMode !== undefined) setPeriodMode(c.periodMode);
    if (c.compare !== undefined) setCompare(c.compare);
    if (c.topN !== undefined) setTopN(c.topN);
    setTag(c.tag ?? '');
  };

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold text-ink">Money Intelligence</h1>
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="space-y-4 lg:w-64 lg:shrink-0">
          <div role="radiogroup" aria-label="Direction" className="flex gap-2">
            {(['income', 'expense'] as const).map((d) => (
              <button
                key={d}
                type="button"
                role="radio"
                aria-checked={direction === d}
                className={`rounded-[var(--radius-control)] border px-3 py-1.5 text-sm capitalize ${
                  direction === d
                    ? 'border-primary bg-primary/10 font-medium text-primary'
                    : 'border-border text-ink hover:bg-bg'
                }`}
                onClick={() => {
                  setDirection(d);
                }}
              >
                {d}
              </button>
            ))}
          </div>

          <label className="block">
            <span className="mb-1 block text-sm text-muted">Dimension</span>
            <select
              className={selectClass}
              value={dimension}
              onChange={(e) => {
                setDimension(e.target.value as MiDimension);
              }}
            >
              {dimensions.map((d) => (
                <option key={d} value={d}>
                  {DIMENSION_LABELS[d]}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-muted">Currency</span>
            <select
              className={selectClass}
              value={currency}
              onChange={(e) => {
                setCurrency(e.target.value as CurrencyCode);
              }}
            >
              {['PKR', 'USD', 'EUR', 'GBP'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-muted">Period</span>
            <select
              className={selectClass}
              value={periodMode}
              onChange={(e) => {
                setPeriodMode(e.target.value as PeriodMode);
              }}
            >
              <option value="month">This month</option>
              <option value="year">This year</option>
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={compare}
              onChange={(e) => {
                setCompare(e.target.checked);
              }}
            />
            Compare to previous {periodMode} (MI-4)
          </label>

          {tags !== undefined && tags.length > 0 && (
            <label className="block">
              <span className="mb-1 block text-sm text-muted">Tag (SR-2)</span>
              <select
                className={selectClass}
                value={tag}
                onChange={(e) => {
                  setTag(e.target.value);
                }}
              >
                <option value="">All tags</option>
                {/* Archived tags stay filterable — same reasoning as Transactions.tsx's own
                    tag filter: archiving doesn't erase historical taggings, so hiding an
                    archived tag here would silently strand already-tagged data. */}
                {tags.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="mb-1 block text-sm text-muted">Top N</span>
            <input
              aria-label="Top N"
              type="number"
              min={1}
              className={inputClass}
              value={topN ?? ''}
              onChange={(e) => {
                const value = e.target.value.trim();
                setTopN(value === '' ? null : Number(value));
              }}
            />
          </label>

          <div className="rounded-[var(--radius-card)] border border-border bg-surface p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
              Saved views (MI-5)
            </p>
            <div className="mb-2 flex gap-2">
              <input
                aria-label="View name"
                className={`${inputClass} flex-1`}
                placeholder="Name this view"
                value={viewName}
                onChange={(e) => {
                  setViewName(e.target.value);
                }}
              />
              <button
                type="button"
                className={ghostButtonInlineClass}
                disabled={createView.isPending || viewName.trim() === ''}
                onClick={() => {
                  createView.mutate(
                    {
                      name: viewName.trim(),
                      config: {
                        direction,
                        dimension,
                        currency,
                        periodMode,
                        compare,
                        topN,
                        ...(tag !== '' ? { tag } : {}),
                      },
                    },
                    {
                      onSuccess: () => {
                        setViewName('');
                      },
                    },
                  );
                }}
              >
                Save view
              </button>
            </div>
            {(savedViews.data ?? []).length === 0 ? (
              <p className="text-sm text-muted">No saved views yet.</p>
            ) : (
              <ul className="space-y-1">
                {(savedViews.data ?? []).map((v) => (
                  <li key={v.id} className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      className="truncate text-sm text-primary hover:underline"
                      onClick={() => {
                        applyView(v.config);
                      }}
                    >
                      {v.name}
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${v.name}`}
                      className="text-xs text-muted hover:text-danger"
                      onClick={() => {
                        deleteView.mutate(v.id);
                      }}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <TrendMiniChart rows={trend?.rows ?? []} direction={direction} />

          {isLoading && <p className="text-sm text-muted">Loading…</p>}

          {breakdown !== undefined && (
            <>
              <p className="mb-3 text-sm text-ink">
                Total: <Amount minorUnits={breakdown.totalMinor} currency={currency} />
              </p>
              {rows.length === 0 ? (
                <p className="text-sm text-muted">No data for this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted">
                        <th className="py-2 pr-4 font-medium">{DIMENSION_LABELS[dimension]}</th>
                        <th className="py-2 pr-4 font-medium">Amount</th>
                        {compare && (
                          <th className="py-2 pr-4 font-medium">vs previous {periodMode}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const priorAmount = priorByKey.get(row.key) ?? 0;
                        const deltaMinor = row.amountMinor - priorAmount;
                        return (
                          <tr key={row.key} className="border-b border-border last:border-0">
                            <td className="py-2 pr-4 text-ink">
                              <Link to="/tx" className="hover:underline">
                                {row.label}
                              </Link>
                            </td>
                            <td className="py-2 pr-4 text-ink">
                              <Amount minorUnits={row.amountMinor} currency={currency} />
                            </td>
                            {compare && (
                              <td
                                className={`py-2 pr-4 ${deltaMinor < 0 ? 'text-danger' : 'text-success'}`}
                              >
                                {deltaMinor >= 0 ? '+' : ''}
                                <Amount minorUnits={deltaMinor} currency={currency} />
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
